import { Plus, X } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import type { OnboardingFormData } from "../../../types/onboarding";
import FieldError from "../../FieldError";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

const errStyle: React.CSSProperties = { borderColor: "var(--color-error-500)" };

const ContactDetailsStep: React.FC = () => {
	const {
		control,
		formState: { errors },
		setValue,
	} = useFormContext<OnboardingFormData>();

	const [showSecondary, setShowSecondary] = useState(false);

	const handleRemoveSecondary = () => {
		setShowSecondary(false);
		setValue("secondary_contact", undefined);
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
			<h2
				style={{
					fontSize: "1.25rem",
					fontWeight: 600,
					color: "var(--color-text-primary)",
					marginBottom: "0.5rem",
				}}
			>
				Contact Details
			</h2>

			{/* Primary Contact */}
			<fieldset
				style={{
					border: "1px solid var(--color-border)",
					borderRadius: "var(--border-radius)",
					padding: "1rem",
				}}
			>
				<legend
					style={{
						fontSize: "0.875rem",
						fontWeight: 500,
						color: "var(--color-text-primary)",
						padding: "0 0.5rem",
					}}
				>
					Primary Contact *
				</legend>
				<div
					style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
				>
					<div>
						<Label htmlFor="primary_contact.name">Full Name</Label>
						<Controller
							name="primary_contact.name"
							control={control}
							render={({ field }) => (
								<Input
									id="primary_contact.name"
									placeholder="Full name"
									{...field}
									style={errors.primary_contact?.name ? errStyle : undefined}
								/>
							)}
						/>
						<FieldError error={errors.primary_contact?.name} />
					</div>
					<div>
						<Label htmlFor="primary_contact.email">Business Email</Label>
						<Controller
							name="primary_contact.email"
							control={control}
							render={({ field }) => (
								<Input
									id="primary_contact.email"
									type="email"
									placeholder="name@company.com"
									{...field}
									style={errors.primary_contact?.email ? errStyle : undefined}
								/>
							)}
						/>
						<FieldError error={errors.primary_contact?.email} />
					</div>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: "0.75rem",
						}}
					>
						<div>
							<Label htmlFor="primary_contact.phone">Phone</Label>
							<Controller
								name="primary_contact.phone"
								control={control}
								render={({ field }) => (
									<Input
										id="primary_contact.phone"
										type="tel"
										placeholder="+1 (555) 000-0000"
										{...field}
										style={errors.primary_contact?.phone ? errStyle : undefined}
									/>
								)}
							/>
							<FieldError error={errors.primary_contact?.phone} />
						</div>
						<div>
							<Label htmlFor="primary_contact.role">Role / Title</Label>
							<Controller
								name="primary_contact.role"
								control={control}
								render={({ field }) => (
									<Input
										id="primary_contact.role"
										placeholder="e.g. CEO, CTO"
										{...field}
										style={errors.primary_contact?.role ? errStyle : undefined}
									/>
								)}
							/>
							<FieldError error={errors.primary_contact?.role} />
						</div>
					</div>
				</div>
			</fieldset>

			{/* Secondary Contact Toggle */}
			{!showSecondary ? (
				<Button
					type="button"
					variant="outline"
					onClick={() => setShowSecondary(true)}
					style={{ alignSelf: "flex-start" }}
				>
					<Plus size={14} style={{ marginRight: "0.35rem" }} />
					Add Secondary Contact
				</Button>
			) : (
				<fieldset
					style={{
						border: "1px solid var(--color-border)",
						borderRadius: "var(--border-radius)",
						padding: "1rem",
						position: "relative",
					}}
				>
					<legend
						style={{
							fontSize: "0.875rem",
							fontWeight: 500,
							color: "var(--color-text-primary)",
							padding: "0 0.5rem",
						}}
					>
						Secondary Contact
					</legend>
					<button
						type="button"
						onClick={handleRemoveSecondary}
						style={{
							position: "absolute",
							top: "0.5rem",
							right: "0.5rem",
							background: "none",
							border: "none",
							cursor: "pointer",
							color: "var(--color-text-secondary)",
						}}
						aria-label="Remove secondary contact"
					>
						<X size={16} />
					</button>
					<div
						style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
					>
						<div>
							<Label htmlFor="secondary_contact.name">Full Name</Label>
							<Controller
								name="secondary_contact.name"
								control={control}
								render={({ field }) => (
									<Input
										id="secondary_contact.name"
										placeholder="Full name"
										{...field}
									/>
								)}
							/>
							<FieldError error={errors.secondary_contact?.name} />
						</div>
						<div>
							<Label htmlFor="secondary_contact.email">Business Email</Label>
							<Controller
								name="secondary_contact.email"
								control={control}
								render={({ field }) => (
									<Input
										id="secondary_contact.email"
										type="email"
										placeholder="name@company.com"
										{...field}
									/>
								)}
							/>
							<FieldError error={errors.secondary_contact?.email} />
						</div>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 1fr",
								gap: "0.75rem",
							}}
						>
							<div>
								<Label htmlFor="secondary_contact.phone">Phone</Label>
								<Controller
									name="secondary_contact.phone"
									control={control}
									render={({ field }) => (
										<Input
											id="secondary_contact.phone"
											type="tel"
											placeholder="+1 (555) 000-0000"
											{...field}
										/>
									)}
								/>
								<FieldError error={errors.secondary_contact?.phone} />
							</div>
							<div>
								<Label htmlFor="secondary_contact.role">Role / Title</Label>
								<Controller
									name="secondary_contact.role"
									control={control}
									render={({ field }) => (
										<Input
											id="secondary_contact.role"
											placeholder="e.g. VP Engineering"
											{...field}
										/>
									)}
								/>
								<FieldError error={errors.secondary_contact?.role} />
							</div>
						</div>
					</div>
				</fieldset>
			)}
		</div>
	);
};

export default ContactDetailsStep;
